import { useCallback, useEffect, useState } from 'react';
import { useUiStore } from '@/stores/ui-store';

/**
 * Sidebar pin behavior:
 *   - `pinned` mirrors `useUiStore().sidePinned`.
 *   - `toggle()` flips it (also bound to ⌘\ globally).
 *   - `hovering` reflects pointer state on the collapsed sidebar; the
 *     consumer applies the pseudo-class equivalent in CSS, but we expose
 *     it for components that want to react in JS (e.g. lazy-load nav
 *     glyphs only when expanded).
 *   - `onMouseEnter` / `onMouseLeave` are noop-safe handlers that flip
 *     the local `hovering` flag.
 *
 * The global ⌘\ / Ctrl+\ keyboard listener is installed exactly once —
 * even if the hook is rendered N times, the listener is reference-counted
 * via a module-level mount counter so we keep one window listener while
 * any consumer is mounted, and remove it when the last one unmounts.
 * Production AppShell mounts a single instance; the singleton scope
 * keeps storybook + tests honest if they happen to mount more than one.
 */

let listenerCount = 0;
let removeListener: (() => void) | null = null;

export function useSidePin() {
  const pinned = useUiStore((s) => s.sidePinned);
  const toggleSidePin = useUiStore((s) => s.toggleSidePin);
  const [hovering, setHovering] = useState(false);

  const toggle = useCallback(() => {
    toggleSidePin();
  }, [toggleSidePin]);

  useEffect(() => {
    listenerCount += 1;
    if (listenerCount === 1) {
      const onKey = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
          e.preventDefault();
          // Read latest from the store at firing time so the singleton
          // listener doesn't capture a stale `toggleSidePin` reference
          // from the first mount.
          useUiStore.getState().toggleSidePin();
        }
      };
      window.addEventListener('keydown', onKey);
      removeListener = () => window.removeEventListener('keydown', onKey);
    }
    return () => {
      listenerCount -= 1;
      if (listenerCount === 0 && removeListener) {
        removeListener();
        removeListener = null;
      }
    };
  }, []);

  const onMouseEnter = useCallback(() => setHovering(true), []);
  const onMouseLeave = useCallback(() => setHovering(false), []);

  return { pinned, toggle, hovering, onMouseEnter, onMouseLeave };
}
